// components/UsedMeters/UsedMetersTable.tsx - Main component file
import { GenericTable } from "@shared/components/GenericTable";
import { toSnakeCase } from "@shared/utils/caseConverter";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Eye, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetUsedMeters, useUsedMetersExport } from "../../hooks";
import { DisposeMeterModal } from "./deleteMeterModal";
import UsedMetersFilterPanel from "./UsedMetersFilterPanel";

interface UsedMeterData {
  id: string;
  deviceNo: string;
  meterTypeDisplay: string;
  productCode: string;
  premiseName: string;
  previousConsumerName: string;
  meterAgeInYears: number;
  lastModifiedDate: string;
  currentReadingDate: string;
}

export interface UsedMetersFilters {
  meter_type?: string;
  product_code?: string;
  ageRange?: string;
  uninstalledDateFrom?: string;
  uninstalledDateTo?: string;
  page?: number;
  limit?: number;
  search_data?: string;
  sort?: string;
  sort_direction?: "asc" | "desc";
}

export function UsedMeters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [selectedMeter, setSelectedMeter] = useState<UsedMeterData | null>(
    null
  );
  const navigate = useNavigate();

  // Parse filters from URL params - following same pattern as PrintingTable
  const getFiltersFromUrl = (): UsedMetersFilters => {
    const urlFilters: UsedMetersFilters = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Parse meter type
    const meterTypeParam = searchParams.get("meter_type");
    if (meterTypeParam && meterTypeParam !== "ALL_METER_TYPES") {
      urlFilters.meter_type = meterTypeParam;
    }

    // Parse utility service
    const utilityServiceParam = searchParams.get("product_code");
    if (utilityServiceParam && utilityServiceParam !== "ALL_UTILITIES") {
      urlFilters.product_code = utilityServiceParam;
    }

    // Parse age range
    const ageRangeParam = searchParams.get("ageRange");
    if (ageRangeParam && ageRangeParam !== "ALL_AGES") {
      urlFilters.ageRange = ageRangeParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    // Parse sorting
    const sortFieldParam = searchParams.get("sort");
    if (sortFieldParam) {
      urlFilters.sort = sortFieldParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<UsedMetersFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    setFilters(getFiltersFromUrl());
  }, [searchParams, currentPage]);

  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam) {
      setCurrentPage(parseInt(pageParam, 10));
    }
  }, [searchParams]);

  // Update sort config from URL
  useEffect(() => {
    const sortFieldParam = searchParams.get("sortField");
    const sortDirectionParam = searchParams.get("sortDirection");
    if (sortFieldParam && sortDirectionParam) {
      setSortConfig({
        field: sortFieldParam,
        direction: sortDirectionParam as "asc" | "desc",
      });
    }
  }, [searchParams]);

  const { data: usedMetersData, isLoading } = useGetUsedMeters(filters);
  const { exportCSV, isExporting } = useUsedMetersExport(filters);

  // Table columns configuration - following same pattern as PrintingTable
  const usedMetersColumns = [
    {
      key: "deviceNo",
      header: "Device Number",
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "meterTypeDisplay",
      header: "Meter Type",
    },
    {
      key: "productCode",
      header: "Utility Service",
    },
    {
      key: "premiseName",
      header: "Previous Premise",
    },
    {
      key: "previousConsumerName",
      header: "Previous Consumer Name",
      sortable: true,
    },
    {
      key: "meterAgeInYears",
      header: "Age",
      headerClassName: "text-center whitespace-nowrap",
      render: (value: number) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {`${value} year${value !== 1 ? "s" : ""}`}
          </span>
        </div>
      ),
    },
    {
      key: "lastModifiedDate",
      header: "Uninstalled On",
      sortable: true,
      headerClassName: "text-center whitespace-nowrap",
    },
    {
      key: "currentReadingDate",
      header: "Last Reading",
      headerClassName: "text-center whitespace-nowrap",
    },
    {
      key: "actions",
      header: "Actions",
      headerClassName: "text-center",
      render: (value: any, row: UsedMeterData) => {
        return (
          <div
            className="flex justify-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              title="View meter details"
              className="p-1 rounded hover:bg-gray-100 text-blue-600 hover:text-blue-800 transition-colors"
              onClick={() =>
                navigate(`/mx/device-management/meter/view/${row.id}`)
              }
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              title="Dispose meter"
              className="p-1 rounded hover:bg-gray-100 text-red-600 hover:text-red-800 transition-colors"
              onClick={() => handleDeleteClick(row)}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
    },
  ];

  const handleApplyFilters = (newFilters: Partial<UsedMetersFilters>) => {
    console.log("newFilters", newFilters);

    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("meter_type");
    params.delete("product_code");
    params.delete("page");

    // Add new filter params
    if (newFilters.meter_type) {
      params.set("meter_type", newFilters.meter_type);
    }
    if (newFilters.product_code) {
      params.set("product_code", newFilters.product_code);
    }
    if (newFilters.ageRange) {
      params.set("ageRange", newFilters.ageRange);
    }
    if (newFilters.uninstalledDateFrom) {
      params.set("uninstalledDateFrom", newFilters.uninstalledDateFrom);
    }
    if (newFilters.uninstalledDateTo) {
      params.set("uninstalledDateTo", newFilters.uninstalledDateTo);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("meter_type");
    params.delete("product_code");

    params.delete("page");
    params.delete("query");
    params.delete("sort");

    setCurrentPage(1);
    setSortConfig(null);
    setSearchParams(params);
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);

    const sortFieldValue = (direction === "asc" ? "+" : "-") + field;
    const params = new URLSearchParams(searchParams);
    params.set("sort", toSnakeCase(sortFieldValue)); // use underscore key
    params.set("page", "1");
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
    setCurrentPage(newPage);
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleExport = () => {
    exportCSV();
  };

  const handleDeleteClick = (row: UsedMeterData) => {
    setSelectedMeter(row);
  };

  const handleConfirmDisposal = () => {
    setSelectedMeter(null);
    // Add disposal logic here if needed
  };

  const handleCloseModal = () => {
    setSelectedMeter(null);
  };

  return (
    <div className="space-y-6">
      <GenericTable
        data={usedMetersData?.results ?? []}
        columns={usedMetersColumns}
        rowKey="id"
        loading={isLoading}
        emptyMessage="No used meters found"
        searchPlaceholder="Search by device number, meter type, or premise..."
        searchParamName="query"
        searchDebounceMs={500}
        onSearch={handleSearch}
        sortConfig={sortConfig}
        onSort={handleSort}
        onExport={handleExport}
        exportLoading={isExporting}
        filterPanel={
          <UsedMetersFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((usedMetersData?.count || 0) / PAGE_SIZE),
          totalItems: usedMetersData?.count || 0,
          itemsPerPage: PAGE_SIZE,
          startItem: currentPage ? (currentPage - 1) * PAGE_SIZE + 1 : 0,
          endItem: currentPage
            ? Math.min(currentPage * PAGE_SIZE, usedMetersData?.count || 0)
            : 0,
        }}
        onPageChange={handlePageChange}
      />

      <DisposeMeterModal
        isOpen={!!selectedMeter}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDisposal}
        meterData={selectedMeter}
      />
    </div>
  );
}
